import React, { memo, useContext, useEffect, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow, } from 'reactflow';
import { WorkflowCanvasContext } from "../../contexts/WorkflowCanvasContextProvider";

import './custom_edge.scss';
import ReactDOM from "react-dom";

const CustomEdge = props => {

    const {
        id,
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        style = {},
        label,
        markerEnd,
        source,
        target
    } = props;

    const { setEdges, getNode } = useReactFlow();
    const workflowContext = useContext(WorkflowCanvasContext);
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
    });

    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
    });

    const [isHovering, setIsHovering] = useState(false);
    const [isSelected, setIsSelected] = useState(false);

    const onEdgeClick = () => {
        setEdges((edges) => edges.filter((edge) => edge.id !== id));
        // Trigger save after edge deletion
        if (workflowContext.autoSaveEnabled) {
            setTimeout(() => {
                workflowContext.saveWorkflow(true);
            }, 100);
        }
    };

    const handleContextMenu = (e) => {
        e.preventDefault();
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
        });
    };

    useEffect(() => {
        const handleClickOutside = () => {
            closeContextMenu();
        };

        if (contextMenu.visible) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [contextMenu.visible]);

    const closeContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0 });
    };

    const handleMouseEnter = () => {
        if (!isSelected) {
            setIsHovering(true);

            // Get the source and target nodes
            const sourceNode = document.querySelector(`[data-id="${source}"]`);
            const targetNode = document.querySelector(`[data-id="${target}"]`);

            // Add blue border to connected nodes with rounded corners
            if (sourceNode) {
                sourceNode.style.boxShadow = '0 0 0 3px #3b82f6';
                sourceNode.style.borderRadius = '8px';
                sourceNode.style.transition = 'all 0.2s ease-in-out';
            }

            if (targetNode) {
                targetNode.style.boxShadow = '0 0 0 3px #3b82f6';
                targetNode.style.borderRadius = '8px';
                targetNode.style.transition = 'all 0.2s ease-in-out';
            }
        }
    };

    const handleMouseLeave = () => {
        if (!isSelected) {
            setIsHovering(false);

            // Get the source and target nodes
            const sourceNode = document.querySelector(`[data-id="${source}"]`);
            const targetNode = document.querySelector(`[data-id="${target}"]`);

            // Remove blue border from connected nodes
            if (sourceNode) {
                sourceNode.style.boxShadow = '';
                sourceNode.style.borderRadius = '';
            }

            if (targetNode) {
                targetNode.style.boxShadow = '';
                targetNode.style.borderRadius = '';
            }
        }
    };

    const handleEdgeClick = (e) => {
        e.stopPropagation();

        // Toggle selected state
        setIsSelected(!isSelected);

        // Get the source and target nodes
        const sourceNode = document.querySelector(`[data-id="${source}"]`);
        const targetNode = document.querySelector(`[data-id="${target}"]`);

        if (!isSelected) {
            // Add more pronounced blue border to connected nodes with rounded corners
            if (sourceNode) {
                sourceNode.style.boxShadow = '0 0 0 5px #3b82f6';
                sourceNode.style.borderRadius = '8px';
                sourceNode.style.transition = 'all 0.2s ease-in-out';
            }

            if (targetNode) {
                targetNode.style.boxShadow = '0 0 0 5px #3b82f6';
                targetNode.style.borderRadius = '8px';
                targetNode.style.transition = 'all 0.2s ease-in-out';
            }
        } else {
            // Remove blue border from connected nodes
            if (sourceNode) {
                sourceNode.style.boxShadow = '';
                sourceNode.style.borderRadius = '';
            }

            if (targetNode) {
                targetNode.style.boxShadow = '';
                targetNode.style.borderRadius = '';
            }
        }
    };

    // Add click outside handler to deselect edge when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Check if the click is outside the edge
            const edgeElement = document.querySelector(`[data-edge-id="${id}"]`);
            if (isSelected && edgeElement && !edgeElement.contains(e.target)) {
                setIsSelected(false);

                // Get the source and target nodes
                const sourceNode = document.querySelector(`[data-id="${source}"]`);
                const targetNode = document.querySelector(`[data-id="${target}"]`);

                // Remove blue border from connected nodes
                if (sourceNode) {
                    sourceNode.style.boxShadow = '';
                    sourceNode.style.borderRadius = '';
                }

                if (targetNode) {
                    targetNode.style.boxShadow = '';
                    targetNode.style.borderRadius = '';
                }
            }
        };

        // if (isSelected) {
        //     document.addEventListener('click', handleClickOutside);
        // }

        // return () => {
        //     document.removeEventListener('click', handleClickOutside);
        // };
    }, [isSelected, id, source, target]);

    const renderContextMenu = () => (
        <div
            style={{
                position: 'fixed',
                top: contextMenu.y + 10,
                left: contextMenu.x + 10,
                zIndex: 1000,
            }}
            className="w-[150px] bg-white rounded-md border border-[#DDE4EA] p-1"
            onClick={(e) => e.stopPropagation()} // Prevent click outside close on menu click
        >
            <div
                onClick={onEdgeClick}
                className="px-1.5 py-1.5 rounded flex items-center justify-between bg-whtie duration-300 hover:bg-[#F3F4F6] cursor-pointer">
                <p className="text-xs text-[#DF2E2E]">Delete</p>
                <p className="text-[10px] text-[#5D6B82]">Del</p>
            </div>
        </div>
    );

    return (
        <>
            <BaseEdge
                path={edgePath}
                markerEnd={markerEnd}
                style={{
                    ...style,
                    stroke: isSelected ? '#3b82f6' : (isHovering ? '#3b82f6' : style.stroke || '#969b9f'),
                    strokeWidth: isSelected ? 6 : (isHovering ? 4 : style.strokeWidth || 3),
                }}
                data-edge-id={id}
            />
            {/* Animated flowing dots when edge is selected */}
            {isSelected && (
                <>
                    {[0, 0.33, 0.66].map((offset, i) => (
                        <circle
                            key={`flow-dot-${id}-${i}`}
                            r="6"
                            fill="#3b82f6"
                            filter="drop-shadow(0 0 2px rgba(59, 130, 246, 0.6))"
                        >
                            <animateMotion
                                dur="2s"
                                repeatCount="indefinite"
                                begin={`${offset * 2}s`}
                                path={edgePath}
                            />
                        </circle>
                    ))}
                </>
            )}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={20}
                onContextMenu={handleContextMenu}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleEdgeClick}
                data-edge-id={id}
                style={{ cursor: 'pointer' }}
            />

            {/* Render the context menu in a portal */}
            {contextMenu.visible &&
                ReactDOM.createPortal(renderContextMenu(), document.body)}
            {/*<EdgeLabelRenderer>*/}
            {/*    {*/}
            {/*        label ? <div*/}
            {/*            style={{*/}
            {/*                position: 'absolute',*/}
            {/*                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,*/}
            {/*                background: "#FFFFFF",*/}
            {/*                zIndex: 9999999,*/}
            {/*                padding: "4px 8px",*/}
            {/*                borderRadius: "4px",*/}
            {/*                color: '#000',*/}
            {/*                pointerEvents: 'all', // To make label interactive*/}
            {/*            }}*/}
            {/*            className="nodrag nopan"*/}
            {/*        >*/}
            {/*            {label}*/}
            {/*        </div> : <div*/}
            {/*            style={{*/}
            {/*                position: 'absolute',*/}
            {/*                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,*/}
            {/*                fontSize: 12,*/}
            {/*                // everything inside EdgeLabelRenderer has no pointer events by default*/}
            {/*                // if you have an interactive element, set pointer-events: all*/}
            {/*                pointerEvents: 'all',*/}
            {/*            }}*/}
            {/*            className="nodrag nopan"*/}
            {/*        >*/}
            {/*            <button className="w-5 h-5 bg-[#969b9f] border border-white text-white cursor-pointer rounded-full text-xs leading-none" onClick={onEdgeClick}>*/}
            {/*                ×*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    }*/}
            {/*</EdgeLabelRenderer>*/}
        </>
    );
}

export default memo(CustomEdge);
